import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { Menu, X, Scale, Phone, Mail, Linkedin, MessageCircle } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_799882c7-8810-4324-b178-f139acb188db/artifacts/ds7j32h0_IMG-20260313-WA0002.jpg";

const navItems = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Services', path: '/services' },
  { name: 'Contact', path: '/contact' },
  { name: 'Book Appointment', path: '/book' },
];

export const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  useEffect(() => {
    // Only trigger animation when path actually changes
    if (prevPathRef.current !== location.pathname) {
      setAnimationKey(prev => prev + 1);
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname]);

  return (
    <div className="min-h-screen courtroom-background">
      {/* Desktop Header - Above folder */}
      <header className="hidden md:block pt-8 px-4 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src={LOGO_URL} 
              alt="D Brooks Law" 
              className="h-14 w-14 object-contain"
              data-testid="logo"
            />
            <div>
              <span className="font-heading text-xl text-white font-semibold tracking-wide">D BROOKS LAW</span>
              <p className="text-[#C9A86C] text-xs tracking-widest uppercase">Attorneys at Law</p>
            </div>
          </div>
          <div className="flex items-center gap-8 text-white/70">
            <a href="tel:876-381-3538" className="flex items-center gap-2 hover:text-[#C9A86C] transition-colors" data-testid="header-phone">
              <Phone size={16} />
              <span className="text-sm font-medium">876-381-3538</span>
            </a>
            <a href="https://wa.me/18763813538" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#C9A86C] transition-colors" data-testid="header-whatsapp">
              <MessageCircle size={16} />
              <span className="text-sm font-medium">WhatsApp</span>
            </a>
            <a href="mailto:info@dbrookslaw.com" className="flex items-center gap-2 hover:text-[#C9A86C] transition-colors" data-testid="header-email">
              <Mail size={16} />
              <span className="text-sm font-medium">info@dbrookslaw.com</span>
            </a>
            <a 
              href="https://jm.linkedin.com/in/devon-brooks-3096b0337" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-[#C9A86C] transition-colors"
              data-testid="header-linkedin"
            >
              <Linkedin size={20} />
            </a>
          </div>
        </div>
      </header>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 z-50 bg-[#1a1412]/95 backdrop-blur-md border-b border-[#C9A86C]/20 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="D Brooks Law" className="h-10 w-10 object-contain" />
            <span className="font-heading text-lg text-white">D Brooks Law</span>
          </div>
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-[#C9A86C] p-2"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav className="absolute top-full left-0 right-0 bg-[#1a1412] border-b border-[#C9A86C]/20 py-4 shadow-xl">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `block px-6 py-3 font-body text-sm uppercase tracking-wider transition-colors ${
                    isActive ? 'text-[#C9A86C] bg-white/10' : 'text-white hover:text-[#C9A86C] hover:bg-white/5'
                  }`
                }
                data-testid={`mobile-nav-${item.name.toLowerCase().replace(' ', '-')}`}
              >
                {item.name}
              </NavLink>
            ))}
          </nav>
        )}
      </header>

      {/* Main Content Area */}
      <main className="px-4 lg:px-8 pb-8 md:pt-6 pt-20 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Folder Tabs - Desktop */}
          <nav className="hidden md:flex items-end gap-0.5 pl-4" data-testid="folder-tabs">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path || 
                (item.path === '/' && location.pathname === '/');
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`
                    folder-tab
                    ${isActive ? 'active' : ''}
                  `}
                  data-testid={`nav-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  {item.name}
                </NavLink>
              );
            })}
          </nav>

          {/* Folder Container with Page Flip */}
          <div className="folder-container paper-texture min-h-[75vh]">
            <div key={animationKey} className="page-content relative">
              <Outlet />
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#0d0a08] text-white py-12 px-4 border-t border-[#C9A86C]/20 relative z-10">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src={LOGO_URL} alt="D Brooks Law" className="h-12 w-12 object-contain" />
                <div>
                  <span className="font-heading text-xl block">D Brooks Law</span>
                  <span className="text-[#C9A86C] text-xs tracking-widest uppercase">Justice & Integrity</span>
                </div>
              </div>
              <p className="text-white/50 font-body text-sm leading-relaxed">
                Serving Jamaica with excellence in Corporate, Civil, Maritime, and Real Estate Law for over 23 years.
              </p>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4 text-[#C9A86C]">Contact</h4>
              <div className="space-y-3 text-white/60 text-sm">
                <p className="flex items-center gap-2"><Phone size={14} /> 876-381-3538</p>
                <a href="https://wa.me/18763813538" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#C9A86C] transition-colors">
                  <MessageCircle size={14} /> WhatsApp Us
                </a>
                <p className="flex items-center gap-2"><Mail size={14} /> info@dbrookslaw.com</p>
                <p>Kingston, Jamaica</p>
              </div>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4 text-[#C9A86C]">Practice Areas</h4>
              <ul className="space-y-2 text-white/60 text-sm">
                <li>Corporate & Business Law</li>
                <li>Civil Litigation</li>
                <li>Maritime Law</li>
                <li>Real Estate Law</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 mt-10 pt-6 text-center text-white/30 text-sm">
            © {new Date().getFullYear()} D Brooks Law. All Rights Reserved.
          </div>
          
          {/* Powered By Footer */}
          <div className="mt-6 pt-4 flex items-center justify-center gap-2">
            <a 
              href="https://www.centurionsoftwaresolutions.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 opacity-40 hover:opacity-70 transition-opacity"
              data-testid="powered-by-link"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_jamaica-law-firm/artifacts/lum3nlk0_ch8te3lk_Untitled%20design%20%2819%29.png" 
                alt="Centurion Software Solutions" 
                className="h-5 w-5 object-contain"
              />
              <span className="text-white/30 text-xs">Powered by Centurion Software Solutions</span>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
